/**
 * How much one room host may be asked to carry, and where every number in here was measured.
 *
 * Three knobs decide the *shape* of a deployment — rooms per process, players per room, and the
 * players one host will serve at once — as opposed to the bot policy in `bots.ts`, which decides
 * what happens inside one room. They live in `@chompdome/shared` because three processes read
 * them: the standalone worker seeds its caps from this table, the embedded host in `apps/server`
 * seeds its own from the other row of it, and the admin console draws the safe range beside each
 * field and refuses to send a value outside it without a confirmation.
 *
 * A second copy of any of this would be a fleet whose halves disagreed about how big it was.
 *
 * ## Two of the three are backstops, and the third is the plan
 *
 * `maxRooms x maxPlayers` is a number an operator reads off the console and it is NOT what the
 * host is provisioned for: 20 x 100 is 2,000 seats on an API process measured to serve ~500, and
 * 100 x 100 is 10,000 on a worker whose comfortable share of a measured fleet is ~200. Those
 * products are deliberate — rooms are created on demand and TARGET_UTILIZATION governs the
 * steady state, so the caps exist to stop a runaway matchmaker pinning a process *before* the
 * utilization signal has had its 40-tick window to react.
 *
 * `maxConcurrentPlayers` is the one that was actually measured, and it is enforced where a seat
 * is handed out: the matchmaker skips a host at its ceiling and the host itself refuses a socket
 * past it. Without it the two backstops were the only numbers on the page, and an operator
 * reading them concluded the box was provisioned for their product.
 *
 * ## The measurements
 *
 * - **A full 100-player room costs 0.032 ms of a 50 ms tick** (`pnpm bench`, `docs/architecture.md`).
 *   Simulation is not what bounds a host; replication is, by a factor of 40-70.
 * - **~500 concurrent players per API process** on the dev machine: serving 500 costs one API
 *   process 24 ms of the tick, of which 0.65 ms is simulation (`docs/architecture.md`). That is
 *   the number to plan an embedded host with — not the rooms-per-core figure `pnpm bench` prints.
 * - **~1,000 concurrent players across FIVE standalone workers** on the production box
 *   (`deploy/README.md`, the five-rung ladder, 2026-08-28): 1,011 seated in 11 rooms, box 56%
 *   busy, busiest worker at 0.48 of its tick, ack p95 64 ms. That is ~200 players and ~2 rooms
 *   per worker at the comfortable figure. The fleet starts missing frames at ~1,100 and at 1,590
 *   one worker needs 55 ms of a 50 ms tick and never catches up.
 *
 *   Note the shape of that number: it is 1,000 per *fleet of five*, not per worker. A single
 *   worker asked to hold a thousand players is the 1,600-player rung, which is the broken one.
 *
 * At the measured comfortable load a worker is running two or three rooms, which is what makes
 * the room caps below backstops rather than expected occupancy.
 */

import type {
  HostKind,
  HostLimitKnob,
  HostLimits,
  LimitRange,
  RoomSettings,
} from '../types/index.js';
import {
  DEFAULT_ROOM_PLAYERS,
  MAX_WORLD_SIZE,
  MIN_WORLD_SIZE,
  ROOM_MAX_PLAYERS,
  WORLD_HEIGHT,
  WORLD_WIDTH,
  pelletCountFor,
  worldAreaRatio,
} from './simulation.js';

/**
 * The two kinds of room host, in the fixed order anything that draws both iterates them.
 * `embedded` is the `GameWorker` inside `apps/server` — the default deployment — and `worker` is
 * a standalone `apps/game-worker` process. They get different room caps because the embedded one
 * is also serving tRPC, auth and the database pool, and it should give up rooms before it gives
 * up logins.
 */
export const HOST_KINDS: readonly HostKind[] = ['embedded', 'worker'];

/**
 * The measured-best values, and what RESTORE DEFAULTS resets a host to.
 *
 * These are exactly the numbers the two hosts have always booted with; naming them here is what
 * makes "restore" mean something an operator can check rather than a second opinion about the
 * same thing. Changing one changes what a fresh process starts with, so change it with a
 * measurement in hand.
 */
export const MEASURED_HOST_LIMITS: Record<HostKind, HostLimits> = {
  embedded: {
    maxRooms: 20,
    maxPlayers: DEFAULT_ROOM_PLAYERS,
    /**
     * ~500 concurrent players per API process: serving 500 cost one process 24.4 ms of the 50 ms
     * tick at 46% mean utilization, of which 0.65 ms was simulation, with ~4 skipped ticks in a
     * 60 s window (`docs/architecture.md`, "Serving cost"). 800 is where ticks start being
     * skipped in earnest and ack p95 doubles from 57 ms to 96 ms.
     */
    maxConcurrentPlayers: 500,
    /**
     * A host takes rooms unless an operator has said otherwise, so the default is unlocked — and
     * RESTORE DEFAULTS therefore *unlocks* a host, exactly as it puts every other knob back.
     * Locking is a reservation somebody made, not a measurement, which is why there is no
     * measured value to restore it to.
     */
    locked: false,
  },
  worker: {
    maxRooms: 100,
    maxPlayers: DEFAULT_ROOM_PLAYERS,
    /**
     * ~200 concurrent players per standalone worker.
     *
     * READ THE SHAPE OF THIS NUMBER. The measured figure in `deploy/README.md` is 1,011 seated
     * across a fleet of FIVE workers — 11 rooms, box 56% busy, busiest worker at 0.48 of its
     * tick, ack p95 64 ms — so one worker's share of it is ~200. It is not 1,000 per worker: a
     * single worker asked to hold a thousand is the 1,590-player rung, where the busiest worker
     * needs 55 ms of a 50 ms tick, skips 1,289 ticks a minute and never catches up.
     *
     * The fleet starts missing frames at ~1,100 seated, which is ~220 each — so 200 is the
     * comfortable figure and the degradation begins a tenth above it.
     */
    maxConcurrentPlayers: 200,
    /** Unlocked, for the reason the embedded host is. */
    locked: false,
  },
};

/**
 * The absolute ceiling on rooms per process, whatever an operator confirms.
 *
 * Five times the standalone backstop: past this the room map itself is the cost, and a number
 * that large is a typo rather than a decision. `hostLimitsPatchSchema` refuses above it outright
 * — a confirmation only reaches values between the measured-safe range and this.
 */
export const MAX_ROOMS_HARD_LIMIT = 5 * MEASURED_HOST_LIMITS.worker.maxRooms;

/**
 * The absolute ceiling on concurrent players per process, whatever an operator confirms.
 *
 * Five times the largest measured host, on `MAX_ROOMS_HARD_LIMIT`'s reasoning: an operator may
 * deliberately leave the measured range on a bigger box, and a number this far outside it is a
 * typo rather than a decision. It is also comfortably above the whole measured fleet (1,011 on
 * five workers), so no legitimate single-host figure can reach it.
 */
export const MAX_CONCURRENT_PLAYERS_HARD_LIMIT =
  5 * MEASURED_HOST_LIMITS.embedded.maxConcurrentPlayers;

/**
 * A room with one seat in it can never hold a duel, which is half the game. It is a legal value
 * for `roomSettingsSchema` (a room is allowed to be shrunk to one seat to empty it), so it is
 * guarded here rather than there.
 */
const MIN_SANE_MAX_PLAYERS = 2;

/**
 * What the measurements support, per knob. Anything outside this needs an explicit confirmation
 * from the operator; the console draws these numbers beside the field they bound.
 *
 * The safe *maximum* is the measured default in all three cases, because every default already
 * sits at the top of what was measured. That is worth saying twice for `maxPlayers`: the ceiling
 * is ROOM_MAX_PLAYERS (200) and the safe maximum is DEFAULT_ROOM_PLAYERS (100), so a room above
 * 100 is permitted and unmeasured — which is exactly what a confirmation is for.
 *
 * Downwards is a capacity decision rather than a correctness one, so the floor is only where a
 * value stops meaning anything.
 */
export const SAFE_HOST_LIMITS: Record<HostKind, Record<HostLimitKnob, LimitRange>> = {
  embedded: {
    maxRooms: { min: 1, max: MEASURED_HOST_LIMITS.embedded.maxRooms },
    maxPlayers: { min: MIN_SANE_MAX_PLAYERS, max: MEASURED_HOST_LIMITS.embedded.maxPlayers },
    maxConcurrentPlayers: {
      min: 1,
      max: MEASURED_HOST_LIMITS.embedded.maxConcurrentPlayers,
    },
  },
  worker: {
    maxRooms: { min: 1, max: MEASURED_HOST_LIMITS.worker.maxRooms },
    maxPlayers: { min: MIN_SANE_MAX_PLAYERS, max: MEASURED_HOST_LIMITS.worker.maxPlayers },
    maxConcurrentPlayers: {
      min: 1,
      max: MEASURED_HOST_LIMITS.worker.maxConcurrentPlayers,
    },
  },
};

/**
 * Seats the two backstops imply between them — the number an operator reads off the console and
 * the one this module exists to stop them reading as a capacity plan.
 *
 * A function rather than a field on `HostLimits` so there is exactly one multiplication: the
 * dialog prints it live as the operator types and `hostLimitWarnings` judges the same product.
 */
export function impliedHostSeats(limits: Pick<HostLimits, 'maxRooms' | 'maxPlayers'>): number {
  return limits.maxRooms * limits.maxPlayers;
}

/** How an operator's host is described in a sentence they are about to read. */
function hostDescription(kind: HostKind): string {
  return kind === 'embedded' ? 'an API process hosting its own rooms' : 'a standalone worker';
}

/**
 * Why a value is outside what was measured, in the words an operator is shown before they
 * confirm it. Empty means everything judged is inside the safe range.
 *
 * Only the fields present in `limits` are judged per-knob: a patch that moves one knob says
 * nothing about the other two, and warning about a number the operator did not touch would train
 * them to click through.
 *
 * `current` is what the host reports now, and it is what makes the *product* judgeable. Seats
 * are `maxRooms x maxPlayers` against `maxConcurrentPlayers`, so no one field can be judged on
 * its own — pass the host's limits and the patch is merged over them; omit it and the product is
 * simply not judged, which is what a caller holding only a patch should get. The console passes
 * it because it has both halves in front of the operator, and `admin.updateLimits` passes it
 * because it is the same judgement and there must not be two of them.
 */
export function hostLimitWarnings(
  kind: HostKind,
  limits: Partial<HostLimits>,
  current?: HostLimits,
): string[] {
  const safe = SAFE_HOST_LIMITS[kind];
  const warnings: string[] = [];

  if (limits.maxRooms !== undefined) {
    if (limits.maxRooms > safe.maxRooms.max) {
      warnings.push(
        `${limits.maxRooms} rooms is above the measured backstop of ${safe.maxRooms.max} for ` +
          `${hostDescription(kind)}. ` +
          'The box runs out of one host’s tick before it runs out of CPU, and a host past its ' +
          'tick budget drops frames for every room on it.',
      );
    } else if (limits.maxRooms < safe.maxRooms.min) {
      warnings.push(`A host must be allowed at least ${safe.maxRooms.min} room.`);
    }
  }

  if (limits.maxPlayers !== undefined) {
    if (limits.maxPlayers > safe.maxPlayers.max) {
      // Permitted, up to ROOM_MAX_PLAYERS, and unmeasured — which is a different sentence from
      // the refusal this used to be, and the reason 101..200 needs a confirmation rather than a
      // schema error. The cost is not bytes: replication is per viewer and interest-bounded, so
      // a 200-player room costs about what two 100-player rooms cost. The cost is *sight*.
      warnings.push(
        `${limits.maxPlayers} players per room is above ${safe.maxPlayers.max}, the size every ` +
          'capacity figure in docs/architecture.md was measured at. Rooms up to ' +
          `${ROOM_MAX_PLAYERS} are permitted and none of them are measured. This is close to ` +
          'capacity-neutral per player — a viewer is replicated a bounded number of nearby ' +
          'entities however large the room is — but in a crowd that budget is spent at shorter ' +
          'range, so a player in a packed room sees less far away than one in a half-empty room.',
      );
    } else if (limits.maxPlayers < safe.maxPlayers.min) {
      warnings.push(
        `${limits.maxPlayers} player per room is below ${safe.maxPlayers.min}: two players are ` +
          'the fewest that can ever meet, so a smaller room can never run a duel.',
      );
    }
  }

  if (limits.maxConcurrentPlayers !== undefined) {
    if (limits.maxConcurrentPlayers > safe.maxConcurrentPlayers.max) {
      warnings.push(
        `${limits.maxConcurrentPlayers} concurrent players is above the ` +
          `${safe.maxConcurrentPlayers.max} measured for ${hostDescription(kind)}` +
          `${
            kind === 'worker'
              ? ' — and note that the 1,000-player figure on record is a fleet of FIVE workers, ' +
                'not one of them'
              : ''
          }. ` +
          'This is the one knob that decides how many people a host actually serves, and past ' +
          'the measurement it skips ticks for every room it is holding.',
      );
    } else if (limits.maxConcurrentPlayers < safe.maxConcurrentPlayers.min) {
      warnings.push(
        `A host must be allowed at least ${safe.maxConcurrentPlayers.min} concurrent player.`,
      );
    }
  }

  if (current !== undefined) {
    const effective: HostLimits = { ...current, ...limits };
    const seats = impliedHostSeats(effective);
    if (seats > effective.maxConcurrentPlayers) {
      warnings.push(
        `${effective.maxRooms} rooms x ${effective.maxPlayers} players is ` +
          `${seats.toLocaleString('en-US')} seats, above the ` +
          `${effective.maxConcurrentPlayers.toLocaleString('en-US')} concurrent players this ` +
          'host will serve. The room cap is a backstop against a runaway matchmaker, not a ' +
          'capacity plan: this host refuses the seat after its ' +
          `${effective.maxConcurrentPlayers.toLocaleString('en-US')}th player whatever the ` +
          'product says. Lower the room cap if you want the two numbers to agree.',
      );
    }
  }

  return warnings;
}

/** One host as the fleet guard below sees it: how it is addressed, and whether it is locked. */
export interface FleetHostLock {
  /** `WorkerNode.id` — the string every operator surface addresses a host by. */
  id: string;
  locked: boolean;
}

/**
 * Why locking THIS host is about to leave the deployment with nowhere to put an overflow player,
 * in the words an operator reads before they confirm it. Empty means somewhere else can still
 * open a room.
 *
 * The fleet guard, and it is deliberately a warning rather than a refusal: a single-box
 * deployment whose rooms are all declared and pinned is a perfectly coherent thing to want, and
 * it is exactly what "only this room on this worker" means when there is only one worker. What
 * is not acceptable is arriving there by accident — a fleet with every host locked refuses the
 * next player who needs a room the roster cannot seat, and that reads as a total outage rather
 * than as the setting it is.
 *
 * Beside `hostLimitWarnings` and shaped like it for the same reason: the console prints these
 * sentences before it sends, and `admin.updateLimits` runs the same function before it accepts —
 * one judgement, not two that can drift.
 *
 * Unlocking never warns, so `lock` is a parameter rather than the caller's business to check.
 */
export function hostLockWarnings(
  workerId: string,
  lock: boolean,
  fleet: readonly FleetHostLock[],
): string[] {
  if (!lock) return [];

  const others = fleet.filter((host) => host.id !== workerId);
  const consequence =
    'A locked host opens no room the matchmaker invents — it still runs, still serves the ' +
    'declared rooms pinned to it and still takes players into rooms it already has. With no ' +
    'unlocked host left, a player the roster cannot seat is told every worker is at capacity.';

  if (others.length === 0) {
    return [
      `'${workerId}' is the only room host in this deployment, so locking it stops any ` +
        `overflow room being created at all. ${consequence}`,
    ];
  }

  if (others.every((host) => host.locked)) {
    return [
      `Every other room host is already locked, so '${workerId}' is the last one that can open ` +
        `a room. ${consequence}`,
    ];
  }

  return [];
}

/**
 * ## The room's world size
 *
 * Not a host knob — a room's walls are `RoomSettings`, edited on the roster declaration and on a
 * host's defaults rather than on its capacity card — but it is judged here, beside the other
 * three, because it is judged the *same way*: a range the numbers were tuned at, a sentence
 * saying what leaving it costs, and a confirmation rather than a refusal. `MIN_WORLD_SIZE` and
 * `MAX_WORLD_SIZE` in `simulation.ts` are the absolute refusals the schema applies; this is the
 * band inside them that nothing has to be explained about.
 *
 * The safe maximum is twice the default per side, which is four times the field: 13 200 pellets
 * and 96 spikes, against the 3 300 and 24 every figure in `docs/architecture.md` was measured
 * with. Past that the pellet field is the dominant cost of a room and it is unmeasured, exactly
 * as a 101..200-seat room is.
 */
export const SAFE_WORLD_SIZE: LimitRange = { min: MIN_WORLD_SIZE, max: 2 * WORLD_WIDTH };

/**
 * Why this room's world size is outside what the field was tuned for, in the words an operator
 * reads before they confirm it. Empty means everything judged is inside the safe range.
 *
 * Shaped like `hostLimitWarnings`, argument for argument, and for its reasons. Only the fields
 * present in `patch` are judged per side, so a change to the bot mix says nothing about the
 * walls — warning about a number the operator did not touch is how a confirmation becomes
 * something they tick without reading. And `current` is what makes the *area* judgeable: the
 * field scales with `worldWidth x worldHeight`, so a patch that moves one side says nothing
 * about the product on its own. Omit it and the area is simply not judged.
 */
export function worldSizeWarnings(
  patch: Partial<RoomSettings>,
  current?: Pick<RoomSettings, 'worldWidth' | 'worldHeight'>,
): string[] {
  const warnings: string[] = [];
  const sides: [number | undefined, string][] = [
    [patch.worldWidth, 'width'],
    [patch.worldHeight, 'height'],
  ];

  for (const [value, axis] of sides) {
    if (value === undefined) continue;
    if (value > SAFE_WORLD_SIZE.max) {
      warnings.push(
        `A world ${axis} of ${value.toLocaleString('en-US')} is above ${SAFE_WORLD_SIZE.max.toLocaleString('en-US')}, ` +
          `twice the ${WORLD_WIDTH.toLocaleString('en-US')} every number in the simulation was ` +
          'tuned at. Pellets and spikes are densities, so the field grows with the *area*: at ' +
          `${MAX_WORLD_SIZE.toLocaleString('en-US')} square a room carries ` +
          `${pelletCountFor(MAX_WORLD_SIZE, MAX_WORLD_SIZE).toLocaleString('en-US')} pellets ` +
          `against ${pelletCountFor(WORLD_WIDTH, WORLD_HEIGHT).toLocaleString('en-US')}. ` +
          'Rooms up to that are permitted and none of them are measured.',
      );
    } else if (value < SAFE_WORLD_SIZE.min) {
      warnings.push(
        `A world ${axis} of ${value.toLocaleString('en-US')} is below ${SAFE_WORLD_SIZE.min.toLocaleString('en-US')}: ` +
          'the spawn margin and the interest radius are absolute distances, so a map this small ' +
          'has almost no interior left to place anything in.',
      );
    }
  }

  if (
    current !== undefined &&
    (patch.worldWidth !== undefined || patch.worldHeight !== undefined)
  ) {
    const worldWidth = patch.worldWidth ?? current.worldWidth;
    const worldHeight = patch.worldHeight ?? current.worldHeight;
    const ratio = worldAreaRatio(worldWidth, worldHeight);
    if (ratio > 4) {
      warnings.push(
        `That is ${ratio.toFixed(1)}x the default field, so this room opens with ` +
          `${pelletCountFor(worldWidth, worldHeight).toLocaleString('en-US')} pellets and ` +
          "costs proportionally more of its host's tick than a default-size one.",
      );
    }
  }

  return warnings;
}

/**
 * ## COMMITTED capacity, and why it is a third number
 *
 * An operator looking at a host has three different figures in front of them and will conflate
 * them unless they are labelled apart:
 *
 * - **COMMITTED** — what the declared rooms PINNED to this host could hold if they all filled.
 *   It is a promise the operator made by pinning them, and it is knowable while the host is
 *   completely idle. This is what the functions below compute.
 * - **CURRENT** — the humans seated on it right now, which is what `maxConcurrentPlayers` is
 *   actually enforced against.
 * - **UTILIZATION** — the measured share of the tick the host is spending, which is a cost, not
 *   a count.
 *
 * The read-out exists because the other two hide the case it catches. A host with six 100-seat
 * rooms pinned to it is over-committed against a 200-player worker ceiling from the moment
 * somebody saved the sixth declaration — and utilization says 0% and current says 0 until the
 * day they all fill at once, which is the day it is too late. It is judged on EVERY host and not
 * only on locked ones: a lock closes a host to rooms the matchmaker invents, it does not stop
 * the declarations already pinned there from waking, and an auto host can be over-committed by
 * its pins before the matchmaker adds a single dynamic room on top.
 *
 * Dynamic rooms are deliberately not counted. They are placed by utilization and reaped when
 * they go idle, so a host's dynamic load is the matchmaker's ongoing decision rather than the
 * operator's standing one — and a figure that moved every time a room opened could not be read
 * as "what have I promised this box".
 */

/**
 * Below this share of the ceiling a host is carrying less than it was provisioned for.
 *
 * Half, and it is a soft judgement rather than a measurement: the point of the band is that an
 * operator can see at a glance which boxes have room for another declaration, and a host holding
 * under half of what it will serve is one of them. The other two verdicts are arithmetic —
 * `committed > ceiling` is over-committed and everything between is well allocated.
 */
export const WELL_ALLOCATED_MIN_SHARE = 0.5;

/** How a host's committed seats read against the ceiling it was measured at. */
export type HostAllocationVerdict =
  | 'unknown'
  | 'under-committed'
  | 'well-allocated'
  | 'over-committed';

/** One host's COMMITTED capacity, against the player ceiling it was measured at. */
export interface HostAllocation {
  /** How many declared rooms are pinned to this host. */
  pinnedRooms: number;
  /** The seats those rooms would hold between them if every one of them filled. */
  committedSeats: number;
  /** The host's `maxConcurrentPlayers`, or null while it is unreachable. */
  ceiling: number | null;
  /** `committedSeats / ceiling` — null with no ceiling to divide by. */
  share: number | null;
  verdict: HostAllocationVerdict;
}

/**
 * Judge what a host has been promised against what it was measured to serve.
 *
 * `pinned` is the declared rooms pinned to this host — their `maxPlayers`, which is the seat
 * count each one opens with, NOT what is seated in a copy that happens to be running. `ceiling`
 * is `HostLimits.maxConcurrentPlayers`, and it is null for a host that did not answer its probe:
 * the commitment is still knowable and still worth printing, but there is nothing to judge it
 * against, which is what `unknown` says.
 */
export function hostAllocation(
  pinned: readonly Pick<RoomSettings, 'maxPlayers'>[],
  ceiling: number | null,
): HostAllocation {
  const committedSeats = pinned.reduce((total, room) => total + room.maxPlayers, 0);
  const base = { pinnedRooms: pinned.length, committedSeats };

  if (ceiling === null) {
    return { ...base, ceiling: null, share: null, verdict: 'unknown' };
  }

  // Strictly above, so a host committed to exactly its ceiling is the last well-allocated one
  // rather than the first over-committed one. That boundary is the whole judgement: the ceiling
  // is the seat this host refuses *past*, so promising precisely it is promising what it has.
  const verdict: HostAllocationVerdict =
    committedSeats > ceiling
      ? 'over-committed'
      : committedSeats >= ceiling * WELL_ALLOCATED_MIN_SHARE
        ? 'well-allocated'
        : 'under-committed';

  return { ...base, ceiling, share: ceiling > 0 ? committedSeats / ceiling : null, verdict };
}
